import { Form, Formik, type FormikHelpers } from 'formik';
import {
  useAddETSTicketCommentMutation,
  useUploadETSTicketAttachmentsMutation,
} from 'gcs-common/clients/api/entities/tickets/tickets-api';
import { useUploadETSAttachments } from 'gcs-common/clients/api/entities/tickets/tickets-api-helpers';
import IconComponent from 'gcs-common/components/Icon/Icon';
import SendButton from 'gcs-common/components/SendButton/SendButton';
import { ICON, ICON_COLOR, ICON_SIZE } from 'gcs-common/constants/IconConstants';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { showSuccessNotification } from '../../../../slices/notifications/ShowNotificationThunk';

import TicketInputComment from '../TicketCommentInput/TicketCommentInput';
import styles from './styles.module.scss';

interface TicketCommentFormProps {
  ticketId: string;
}

export interface TicketCommentFormValues {
  comment: string;
  files: File[];
}

const TicketCommentForm = (props: TicketCommentFormProps) => {
  const { ticketId } = props;
  const dispatch = useDispatch();
  const addETSTicketComment = useAddETSTicketCommentMutation();
  const uploadETSTicketAttachments = useUploadETSTicketAttachmentsMutation();
  const uploadETSAttachments = useUploadETSAttachments();
  const addCommentLoading = addETSTicketComment.isPending;
  const uploadAttachmentLoading = uploadETSTicketAttachments.isPending;
  const loading = addCommentLoading || uploadAttachmentLoading;

  const initialValues: TicketCommentFormValues = {
    comment: '',
    files: [],
  };

  const validationSchema = Yup.object().shape({
    // comment text is mandatory (we can't just let the user upload attachments without text),
    // because Jira automation relies on a text comment being created for reopening a closed ticket
    comment: Yup.string().required('Erforderlich').matches(/\S/g, 'Erforderlich'),
  });

  const submit = async (
    values: TicketCommentFormValues,
    { setSubmitting, resetForm }: FormikHelpers<TicketCommentFormValues>,
  ) => {
    const { comment, files } = values;
    try {
      await addETSTicketComment.mutateAsync({
        path: {
          ticket_id: ticketId,
        },
        body: {
          body: comment,
        },
      });
      dispatch(showSuccessNotification('Kommentar erstellt'));
      resetForm();
      try {
        await uploadETSAttachments.uploadFiles(ticketId, files);
      } catch (_e) {
        // Error uploading one or more attachments
      }
    } catch (_e) {
      // Error adding comment
    }
    setSubmitting(false);
  };

  const handleCancel = (resetForm: VoidFunction) => {
    return () => {
      resetForm();
    };
  };

  return (
    <Formik<TicketCommentFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      isInitialValid={false}
      onSubmit={submit}
    >
      {({ values, resetForm, isValid }) => (
        <Form className={styles.commentForm}>
          <TicketInputComment />

          <div className={styles.buttonWrapper}>
            <SendButton type="submit" disabled={!isValid || loading} isLoading={loading} />
            {values.files.length ? (
              <button
                type="button"
                className={styles.cancelButton}
                onClick={handleCancel(resetForm)}
                disabled={loading}
              >
                <IconComponent Icon={ICON.CLOSE} size={ICON_SIZE.SMALL} color={ICON_COLOR.ERROR} />
              </button>
            ) : null}
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default TicketCommentForm;
